import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useRevampProductsStore } from "../store";
import { filterProducts } from "../helpers";
import ProductsSearchBar from "./ProductsSearchBar";
import ProductsSection from "./ProductsSection";
import ProductDetailsDrawer from "./ProductDetailsDrawer";
import ProductSkeleton from "./ProductSkeleton";

const RevampProductsDrawer = ({ visible, onClose }) => {
  const { groupedProducts, productsLoading, fetchProductsAndMapping } =
    useRevampProductsStore();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [detailsVisible, setDetailsVisible] = useState(false);

  useEffect(() => {
    if (visible) {
      fetchProductsAndMapping();
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [visible]);

  const filteredGroups = useMemo(
    () => filterProducts(groupedProducts, searchQuery),
    [groupedProducts, searchQuery]
  );

  const totalCount = useMemo(
    () =>
      Object.values(filteredGroups).reduce(
        (sum, products) => sum + products.length,
        0
      ),
    [filteredGroups]
  );

  const sectionKeys = useMemo(
    () => {
      const keys = Object.keys(filteredGroups);
      const others = keys.filter((k) => k === "Others");
      const rest = keys.filter((k) => k !== "Others");
      return [...rest, ...others];
    },
    [filteredGroups]
  );

  const handleProductSelect = useCallback((product) => {
    setSelectedProduct(product);
    setDetailsVisible(true);
  }, []);

  const handleDetailsClose = useCallback(() => {
    setDetailsVisible(false);
    setTimeout(() => setSelectedProduct(null), 300);
  }, []);

  if (!visible) return null;

  return (
    <>
      <div
        className={`revamp-drawer-overlay ${visible ? "revamp-drawer-overlay--visible" : ""}`}
        onClick={onClose}
      />
      <div className={`revamp-drawer ${visible ? "revamp-drawer--open" : ""}`}>
        <div className="revamp-drawer__header">
          <div className="revamp-drawer__header-top">
            <div>
              <h2 className="revamp-drawer__title">Product Catalog</h2>
              <p className="revamp-drawer__subtitle">
                Browse our complete range of health and wellness products
              </p>
            </div>
            <button className="revamp-drawer__close" onClick={onClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <ProductsSearchBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            totalCount={totalCount}
          />
        </div>

        <div className="revamp-drawer__body">
          {productsLoading ? (
            <ProductSkeleton sections={3} />
          ) : sectionKeys.length > 0 ? (
            <div className="revamp-drawer__sections">
              {sectionKeys.map((sectionKey) => (
                <ProductsSection
                  key={sectionKey}
                  title={sectionKey}
                  products={filteredGroups[sectionKey]}
                  onProductSelect={handleProductSelect}
                />
              ))}
            </div>
          ) : (
            <div className="revamp-drawer__empty">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d0d0d0" strokeWidth="1.5">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <h3>No products found</h3>
              <p>Try adjusting your search to find what you are looking for.</p>
            </div>
          )}
        </div>

        <ProductDetailsDrawer
          product={selectedProduct}
          visible={detailsVisible}
          onClose={handleDetailsClose}
        />
      </div>
    </>
  );
};

export default RevampProductsDrawer;
